import { env } from "@/lib/env"

export type Outage = "offline" | "maintenance" | "unreachable"

type Recovery = () => void

const PROBE_URL = new URL("/up", env.apiUrl).toString()
const FIRST_RETRY = 2_000
const LONGEST_RETRY = 30_000

let outage: Outage | null = null
let isChecking = false
let retryDelay = FIRST_RETRY
let retryTimer: ReturnType<typeof setTimeout> | undefined
let probeInFlight: Promise<void> | null = null
let onRecovery: Recovery = () => {}
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) {
    listener()
  }
}

async function ask(): Promise<Outage | null> {
  if (!navigator.onLine) {
    return "offline"
  }

  try {
    const response = await fetch(PROBE_URL, {
      cache: "no-store",
      headers: { accept: "application/json" },
    })

    if (response.ok) {
      return null
    }

    return response.status === 503 ? "maintenance" : "unreachable"
  } catch {
    return navigator.onLine ? "unreachable" : "offline"
  }
}

function scheduleRetry(): void {
  clearTimeout(retryTimer)
  retryTimer = setTimeout(() => void probe(), retryDelay)
  retryDelay = Math.min(retryDelay * 2, LONGEST_RETRY)
}

function probe(): Promise<void> {
  probeInFlight ??= (async () => {
    isChecking = true
    emit()

    const found = await ask()
    const wasDown = outage !== null

    outage = found
    isChecking = false

    if (found) {
      scheduleRetry()
    } else {
      clearTimeout(retryTimer)
      retryDelay = FIRST_RETRY
    }

    emit()

    if (wasDown && !found) {
      onRecovery()
    }
  })().finally(() => {
    probeInFlight = null
  })

  return probeInFlight
}

export function reportServerTrouble(): void {
  if (outage === null) {
    void probe()
  }
}

export function retryNow(): void {
  retryDelay = FIRST_RETRY
  void probe()
}

export function setRecoveryHandler(handler: Recovery): void {
  onRecovery = handler
}

export function subscribeToOutage(listener: () => void): () => void {
  listeners.add(listener)

  return () => listeners.delete(listener)
}

export function currentOutage(): Outage | null {
  return outage
}

export function isCheckingOutage(): boolean {
  return isChecking
}

if (typeof window !== "undefined") {
  window.addEventListener("offline", () => reportServerTrouble())
  window.addEventListener("online", () => {
    if (outage !== null) {
      retryNow()
    }
  })
}
